import { useMutation, useQueryClient } from '@tanstack/react-query';
import { formatUnicorn } from '@dealstack/shared/lib/format/formatUnicorn';
import API_PATHS from '@dealstack/shared/lib/generated/apiPaths';
import {
	getUserFacingErrorMessage,
	makeRequest,
} from '@dealstack/frontend/shared/dataExchange/ApiLayer/apiLayer';
import { HTTP_METHOD } from '@dealstack/shared/lib/common/dataExchange/common';
import { SubmitFormDto } from '@dealstack/shared/lib/generated';
import { showToastV2 } from '@dealstack/frontend/shared/mechanism/toast/useToastV2';
import { DocumentService } from '@dealstack/frontend/shared/dataExchange/services/document/document.service';
import { cleanFileName } from '@dealstack/shared/lib/common/file/file';
import { useSubmitFormUploadUrl } from '@dealstack/frontend/shared/modules/document/signing/hooks/useSubmitFormUploadUrl';
import { useTranslationMipManage } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { calculateMD5ForFile } from '@dealstack/frontend/shared/components/Documents/UploadDocuments/utils/calculateMD5ForFile';
import { Response } from '@dealstack/shared/lib/common/dataExchange/apiV1';

const submitFormRequest = async ({
	dealId,
	programmeId,
	documentWorkflowInstanceId,
	body,
}: {
	dealId: string;
	programmeId: string;
	documentWorkflowInstanceId: string;
	body: SubmitFormDto;
}) => {
	return await makeRequest<Response<void>>({
		endpoint: formatUnicorn(
			API_PATHS.DEAL_DEALID_MIP_PROGRAMMEID_DOCUMENT_WORKFLOW_DOCUMENTWORKFLOWINSTANCEID_SUBMIT_FORM_POST,
			{ dealId, programmeId, documentWorkflowInstanceId }
		),
		method: HTTP_METHOD.POST,
		body: JSON.stringify(body),
	});
};

export const useSubmitSignableForm = (options: {
	dealId: string;
	programmeId: string;
	documentWorkflowInstanceId: string;
}) => {
	const tMipManage = useTranslationMipManage();
	const { dealId, programmeId, documentWorkflowInstanceId } = options;
	const queryClient = useQueryClient();
	const submitFormUploadUrl = useSubmitFormUploadUrl({
		dealId,
		programmeId,
		documentWorkflowInstanceId,
	});

	const submitFormMutation = useMutation({
		mutationFn: submitFormRequest,

		onSuccess: () => {
			return queryClient.invalidateQueries({
				queryKey: [
					formatUnicorn(
						API_PATHS.DEAL_DEALID_MIP_PROGRAMMEID_DOCUMENT_WORKFLOW_DOCUMENTWORKFLOWINSTANCEID_GET,
						{ dealId, programmeId, documentWorkflowInstanceId }
					),
				],
			});
		},
	});

	const handleSubmitForm = async ({
		selectedFile,
		investorId,
	}: {
		selectedFile: File;
		investorId: string;
	}) => {
		const uploadFileData = {
			extension: DocumentService.getFileExtensionFromName(selectedFile.name),
			mimeType: selectedFile.type,
			fileName: cleanFileName(selectedFile.name),
		};

		const preSignedUrlData = await submitFormUploadUrl.mutateAsync({
			fileData: uploadFileData,
			investorId,
		});
		if (!preSignedUrlData) {
			throw new Error(tMipManage('documents_page.toast.error.upload_url_not_available'));
		}

		const md5 = await DocumentService.putUploadToPreSignedUrl({
			preSignedUrl: preSignedUrlData.uploadPreSignedUrl,
			file: selectedFile,
		});

		const fileMd5 = await calculateMD5ForFile(selectedFile, 'error calculating file MD5');

		if (md5 !== `"${fileMd5}"`) {
			throw new Error(tMipManage('documents_page.toast.error.file_upload_mismatch'));
		}

		const fileData = {
			md5: fileMd5,
			fileKey: preSignedUrlData.fileKey,
			fileExtension: uploadFileData.extension,
			mimeType: uploadFileData.mimeType,
			fileName: uploadFileData.fileName,
		};

		return await submitFormMutation.mutateAsync(
			{
				dealId,
				programmeId,
				documentWorkflowInstanceId,
				body: {
					fileData,
					investorId,
				},
			},
			{
				onSuccess: () => {
					showToastV2({
						title: tMipManage('documents_page.toast.success.form_submitted'),
						state: 'success',
					});
				},
				onError: (error: Error) => {
					showToastV2({
						title:
							getUserFacingErrorMessage(error) ??
							tMipManage('documents_page.toast.error.failed_to_sign_document'),
						state: 'error',
					});
				},
			}
		);
	};

	return {
		handleSubmitForm,
		submitFormIsLoading: submitFormMutation.isPending,
	};
};
